<template>
  <div>
    <!--------------------------------------------------------->
    <!-- ACTUAL RECURRING ------------------------------------->
    <!--------------------------------------------------------->
    <v-card class="rounded-lg custom_panel pa-2" flat>
      <v-row no-gutters>
        <v-col cols="12" class="d-flex justify-space-around">
          <span class="subtitle-1 text_color">
            {{ $t("subscriptions.edit.actual_recurring") }}
          </span>
          <span class="subtitle-1">
            {{
              subscription["amount"]
                | valueFormatCurrency(subscription.gateway.currency.symbol)
            }}
          </span>
        </v-col>
      </v-row>
    </v-card>
    <!--------------------------------------------------------->
    <!-- NEW RECURRING ---------------------------------------->
    <!--------------------------------------------------------->
    <v-card class="rounded-lg custom_panel pa-2 my-5" flat>
      <v-card-title class="pa-2">
        <v-row no-gutters>
          <v-col cols="12" class="d-flex justify-center align-center">
            <span class="subtitle-1 text_color">{{
              $t("subscriptions.edit.new_recurring")
            }}</span>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <!--------------------------------------------------------->
        <!-- UPDATE AMOUNT ---------------------------------------->
        <!--------------------------------------------------------->
        <v-row no-gutters class="my-4" v-show="canUpdateAmount">
          <v-col cols="12" md="6" class="d-flex justify-center align-center">
            <span class="subtitle-1 text_color">
              {{ $t("recurring_debit.details.table.amount") }}
            </span>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-center">
            <v-text-field
              v-model="localSubscription.amount"
              v-validate="'required|decimal:2'"
              type="number"
              class="rounded-lg"
              hide-details
              outlined
              dense
              :suffix="localSubscription.gateway.currency.code"
            >
            </v-text-field>
          </v-col>
        </v-row>
        <!--------------------------------------------------------->
        <!-- PAYMENT DATE ---------------------------------------->
        <!--------------------------------------------------------->
        <v-row no-gutters class="my-4" v-show="canUpdateNextPaymentDate">
          <v-col cols="12" md="6" class="d-flex justify-center align-center">
            <span class="subtitle-1 text_color">
              {{ $t("subscriptions.edit.day_month_collection") }}
            </span>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-center">
            <v-menu
              ref="menu"
              :close-on-content-click="false"
              :return-value.sync="localSubscription.next_payment_date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="localSubscription.next_payment_date"
                  v-validate="'required|decimal:2'"
                  type="date"
                  class="rounded-lg"
                  append-icon="calendar_month"
                  readonly
                  hide-details
                  outlined
                  dense
                  v-on="on"
                  v-bind="attrs"
                >
                </v-text-field>
              </template>
              <v-date-picker
                v-model="localSubscription.next_payment_date"
                v-validate="'required'"
                class="rounded-lg"
                hide-details
                outlined
                dense
                color="#2661FF"
                :min="today"
              >
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="#2661FF"
                  @click="$refs.menu.save(localSubscription.next_payment_date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <!--------------------------------------------------------->
        <!-- GATEWAY ---------------------------------------------->
        <!--------------------------------------------------------->
        <v-row no-gutters class="my-4">
          <v-col cols="12" md="6" class="d-flex justify-center align-center">
            <span class="subtitle-1 text_color">
              {{ $t("transactions.details.gateway") }}
            </span>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-center align-center">
            <v-card
              class="rounded-lg d-flex justify-center align-center py-2"
              style="border: 1.5px solid #9d9e9e; width: 100%;"
              flat
            >
              <span class="subtitle-1 text_color">
                {{ localSubscription.gateway.gateway.name }}
              </span>
              <v-img
                :src="getGatewayImage()"
                alt="Gateway image"
                class="ml-2"
                max-height="70"
                max-width="80"
              />
            </v-card>
          </v-col>
        </v-row>
        <!--------------------------------------------------------->
        <!-- INFO ------------------------------------------------->
        <!--------------------------------------------------------->
        <v-row no-gutters class="mt-8">
          <v-col cols="12" class="d-flex justify-center align-center">
            <span class="body-2 text_color text-center">
              {{ $t("subscriptions.edit.info") }}
            </span>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-row no-gutters>
          <v-col cols="12" class="d-flex justify-end align-center">
            <v-btn
              class="rounded-lg"
              color="#2661FF"
              @click="save"
              :loading="isLoading"
              :disabled="!canUpdateSubscription"
            >
              <span style="color: #FFF;">
                {{ $t("subscriptions.edit.update") }}
              </span>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import GatewaysMixin from "@/mixins/gateways";

export default {
  name: "EditPaymentsWaySubscription",
  mixins: [GatewaysMixin],
  data() {
    return {
      isLoading: false,
      localSubscription: {},
      today: this.$moment().format("YYYY-MM-DD")
    };
  },
  created() {
    // Copy the subscription state
    this.localSubscription = {
      ...this.subscription,
      next_payment_date: this.formatDate(this.subscription.next_payment_date)
    };
  },
  computed: {
    ...mapState({
      subscription: ({ transactions: { transaction } }) => transaction
    }),
    canUpdateAmount() {
      const { gateway } = this.subscription.gateway;
      const NOT_ALLOWED_EDIT_AMOUNT = ["SDS"];

      if (gateway.info.includes("EDIT_SUBSCRIPTION")) {
        return !NOT_ALLOWED_EDIT_AMOUNT.includes(gateway.name);
      }

      return false;
    },
    canUpdateNextPaymentDate() {
      const { gateway } = this.subscription.gateway;
      const NOT_ALLOWED_EDIT_NEXT_PAYMENT_DATE = ["Braintree", "SDS"];

      if (gateway.info.includes("EDIT_SUBSCRIPTION")) {
        return !NOT_ALLOWED_EDIT_NEXT_PAYMENT_DATE.includes(gateway.name);
      }

      return false;
    },
    canUpdateSubscription() {
      const { gateway } = this.subscription.gateway;

      return gateway.info.includes("EDIT_SUBSCRIPTION");
    }
  },
  methods: {
    ...mapActions({
      updateSubscription: "transactions/updateSubscription"
    }),
    formatDate(date) {
      return this.$moment(date).format("YYYY-MM-DD");
    },
    getGatewayImage() {
      const { gateway } = this.subscription.gateway;
      return this.getGatewayImageByName(gateway.name);
    },
    async save() {
      const {
        gateway,
        transaction_gateway_code,
        gateway_currency_id,
        organization_id
      } = this.localSubscription;

      try {
        this.isLoading = false;

        await this.updateSubscription({
          ...this.localSubscription,
          id: this.localSubscription.id,
          gateway_id: gateway.gateway.id,
          subscription_id: transaction_gateway_code,
          gateway_currency_id,
          organization_id
        });
      } catch (error) {
        this.$noty.error(
          `${this.$t("subscriptions.edit.error")} ${error.message}`
        );
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style>
.text_color {
  color: #344054 !important;
}

.custom_panel {
  border: 1px solid #d0d3db !important;
}
</style>
